import { useState, useEffect, useMemo } from 'react';
import { isNativeIOS } from '@/utils/platform';

export interface CentralizedMobileDetection {
  // Device Classification
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  
  // Platform Detection
  isIOS: boolean;
  isAndroid: boolean;
  isNativeIOS: boolean;
  
  // Responsive Breakpoints
  screenSize: 'mobile' | 'tablet' | 'desktop';
  viewportWidth: number;
  viewportHeight: number;
  orientation: 'portrait' | 'landscape';
  
  // Safe Areas & Native Features
  safeAreas: { top: number; bottom: number; left: number; right: number };
  availableHeight: number;
  
  // Dialog Positioning Logic
  dialogPosition: 'bottom' | 'center';
  shouldUseMobileLayout: boolean;
}

const MOBILE_BREAKPOINT = 768;
const TABLET_BREAKPOINT = 1024;

// Get safe area values from CSS env() variables with fallbacks
const getSafeAreas = () => {
  if (typeof window === 'undefined') return { top: 0, bottom: 0, left: 0, right: 0 };
  
  try {
    const computedStyle = getComputedStyle(document.documentElement);
    return {
      top: parseInt(computedStyle.getPropertyValue('--safe-area-inset-top') || '0') || 0,
      bottom: parseInt(computedStyle.getPropertyValue('--safe-area-inset-bottom') || '0') || 0,
      left: parseInt(computedStyle.getPropertyValue('--safe-area-inset-left') || '0') || 0,
      right: parseInt(computedStyle.getPropertyValue('--safe-area-inset-right') || '0') || 0,
    };
  } catch {
    return { top: 0, bottom: 0, left: 0, right: 0 };
  }
};

// Detect device characteristics from user agent
const getDeviceInfo = () => {
  if (typeof window === 'undefined') {
    return { isIOS: false, isAndroid: false, isTabletDevice: false };
  }

  const ua = navigator.userAgent.toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(ua) || (/like mac os x/.test(ua) && /iphone|ipad|ipod/.test(ua));
  const isAndroid = /android/.test(ua);
  const isTabletDevice = /ipad/.test(ua) || (/android/.test(ua) && !/mobile/.test(ua)) || 
    (window.innerWidth >= TABLET_BREAKPOINT && window.innerWidth < 1200);

  return { isIOS, isAndroid, isTabletDevice };
};

export const useCentralizedMobileDetection = (): CentralizedMobileDetection => {
  const [viewportWidth, setViewportWidth] = useState(() => 
    typeof window !== 'undefined' ? window.innerWidth : MOBILE_BREAKPOINT
  );
  const [viewportHeight, setViewportHeight] = useState(() => 
    typeof window !== 'undefined' ? window.innerHeight : 800
  );
  const [nativeIOS, setNativeIOS] = useState(false);

  // Update viewport dimensions on resize
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const updateViewport = () => {
      setViewportWidth(window.innerWidth);
      setViewportHeight(window.innerHeight);
    };

    const handleResize = () => {
      updateViewport();
    };

    const handleOrientationChange = () => {
      // Delay to ensure dimensions are updated after orientation change
      setTimeout(updateViewport, 100);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);
    
    // Initial update
    updateViewport();

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);

  // Check for native iOS app
  useEffect(() => {
    isNativeIOS().then(setNativeIOS).catch(() => setNativeIOS(false));
  }, []);

  return useMemo(() => {
    const { isIOS, isAndroid, isTabletDevice } = getDeviceInfo();
    const safeAreas = getSafeAreas();

    // Device classification based on viewport width and device detection
    const isMobile = viewportWidth < MOBILE_BREAKPOINT && !isTabletDevice;
    const isTablet = (viewportWidth >= MOBILE_BREAKPOINT && viewportWidth < TABLET_BREAKPOINT) || isTabletDevice;
    const isDesktop = viewportWidth >= TABLET_BREAKPOINT && !isTabletDevice;

    // Screen size classification
    const screenSize: 'mobile' | 'tablet' | 'desktop' = 
      isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop';

    // Orientation detection
    const orientation: 'portrait' | 'landscape' = 
      viewportHeight > viewportWidth ? 'portrait' : 'landscape';

    // Available height calculation (subtract safe areas)
    const availableHeight = viewportHeight - safeAreas.top - safeAreas.bottom;

    // Dialog positioning logic - mobile uses bottom sheet, tablet/desktop uses center modal
    const dialogPosition: 'bottom' | 'center' = isMobile ? 'bottom' : 'center';
    const shouldUseMobileLayout = isMobile;

    return {
      // Device Classification
      isMobile,
      isTablet,
      isDesktop,
      
      // Platform Detection
      isIOS,
      isAndroid,
      isNativeIOS: nativeIOS,
      
      // Responsive Breakpoints
      screenSize,
      viewportWidth,
      viewportHeight,
      orientation,
      
      // Safe Areas & Native Features
      safeAreas,
      availableHeight,
      
      // Dialog Positioning Logic
      dialogPosition,
      shouldUseMobileLayout,
    };
  }, [viewportWidth, viewportHeight, nativeIOS]);
};